import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { API_URI } from "../../../constants/GlobalConstants";
import Loader from "../../layout/Loader";
import { OrderDetailAction } from "../../../redux/actions/OrderAction";
import { getDealerOrders } from "../../../redux/actions/DealerAction";

function EditPayment({ selected, isPaymentInput, setIsPaymentInput, variables }) {
  const dispatch = useDispatch();
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const detail = useSelector((state) => state.orderDetail);
  const { loading: orderLoading, order } = detail;

  const [loading, setLoading] = useState(false);
  const [updatedPayment, setUpdatedPayment] = useState("");
  const [saveToCredit, setSaveToCredit] = useState(null);

  useEffect(() => {
    if (selected.length > 0) {
      dispatch(OrderDetailAction(selected[0], userInfo.token));
    }
  }, [dispatch, selected, userInfo.token]);

  const payBalance = order?.payBalance || 0;
  const newPayment = parseFloat(updatedPayment) || 0;
  const showCreditOptions = updatedPayment && newPayment < payBalance;

  const handleSubmit = async () => {
    if (!updatedPayment) {
      alert("Please enter a payment amount.");
      return;
    }

    if (showCreditOptions && saveToCredit === null) {
      alert("Please choose whether to save to credit or not.");
      return;
    }

    setLoading(true);
    const payload = {
      selected: selected[0],
      updatedPayment: newPayment,
      saveToCredit: showCreditOptions ? saveToCredit : null,
    };

    try {
      const { data } = await axios.put(`${API_URI}/api/order/update-payment`, payload, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      if (data) {
        dispatch(getDealerOrders(variables));
        setLoading(false);
        setIsPaymentInput(false);
      }
    } catch (error) {
      alert("😭 Oops! Internal Error, contact the Admin 🤓");
      setLoading(false);
    }
  };

  return (
    <Modal open={isPaymentInput} onClose={() => setIsPaymentInput(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "#ffffff",
          color: "#000000",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          border: "none",
        }}
      >
        {loading || orderLoading ? (
          <Loader />
        ) : (
          <>
            <Typography variant="h6" sx={{ color: "#000000", mb: 3 }}>
              Edit Payment
            </Typography>

            <TextField
              fullWidth
              type="number"
              label="Enter Payment Amount"
              variant="outlined"
              value={updatedPayment}
              onChange={(e) => setUpdatedPayment(e.target.value)}
              sx={{ mb: 3 }}
            />

            {showCreditOptions && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#555555", mb: 1, fontStyle: "italic" }}
                >
                  The new amount is less than the paid balance ({payBalance}). Would you like to save the difference to credit?
                </Typography>
                <ToggleButtonGroup
                  fullWidth
                  value={saveToCredit}
                  exclusive
                  onChange={(e, newValue) => newValue !== null && setSaveToCredit(newValue)}
                  sx={{
                    display: "flex",
                    gap: 1,
                    "& .MuiToggleButton-root": {
                      flex: 1,
                      borderRadius: 1,
                      borderColor: "#cccccc",
                      color: "#000000",
                      "&.Mui-selected": {
                        bgcolor: "#1976d2",
                        color: "#ffffff",
                        "&:hover": {
                          bgcolor: "#1565c0",
                        },
                      },
                      "&:hover": {
                        bgcolor: "#f5f5f5",
                      },
                    },
                  }}
                >
                  <ToggleButton value={true}>Save to Credit</ToggleButton>
                  <ToggleButton value={false}>Don't Save</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmit}
                sx={{ minWidth: 100 }}
              >
                Save Payment
              </Button>
              <Button
                variant="outlined"
                onClick={() => setIsPaymentInput(false)}
                sx={{ minWidth: 100 }}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}

EditPayment.propTypes = {
  selected: PropTypes.array.isRequired,
  isPaymentInput: PropTypes.bool.isRequired,
  setIsPaymentInput: PropTypes.func.isRequired,
  variables: PropTypes.object.isRequired,
};

export default EditPayment;